/**
 * useCamera — DocumentCameraModal preset
 *
 * Pre-configured CameraModal for receipt/document capture with ML edge
 * detection, auto-capture, auto-zoom, perspective correction, and dark
 * mode enhancement. Designed for expense receipts, invoices, and till slips.
 *
 * Sensible defaults (all overridable):
 *   - guide mode: 'auto' (ML edge detection with dynamic bounding box)
 *   - autoCapture: true
 *   - autoZoom: true
 *   - reviewCapture: true
 *   - perspectiveCorrection: true
 *   - darkModeEnhance: true
 *   - cropToGuide: true
 *   - documentProfile: 'auto'
 *
 * Usage:
 * ```tsx
 * const camera = useCamera({ onCapture: (b64) => ..., reviewCapture: true });
 * return <DocumentCameraModal camera={camera} title="Receipt Photo" />;
 * ```
 */

import React from 'react';
import { CameraModal } from './CameraModal';
import type { UseCameraReturn, CaptureShortcut, CaptureModeButton } from './types';

export interface DocumentCameraModalProps {
  /** useCamera hook return value */
  camera: UseCameraReturn;
  /** Title shown in header (default: 'Take Photo') */
  title?: string;
  /** Subtitle shown in the guide overlay (default: 'Position receipt within the frame') */
  subtitle?: string;
  /** Document profile hint for auto-zoom behavior (default: 'auto') */
  documentProfile?: 'till-slip' | 'a4' | 'auto';
  /** Guide mode (default: 'auto' — ML edge detection with dynamic bounding box) */
  guideMode?: 'auto' | 'receipt' | 'invoice' | 'continuous';
  /** Enable auto-capture when document edge is stable (default: true) */
  autoCapture?: boolean;
  /** Enable auto-zoom for document framing (default: true) */
  autoZoom?: boolean;
  /** Show review screen after capture (default: true) */
  reviewCapture?: boolean;
  /** Enable batch/multi-page capture (default: false) */
  batchCapture?: boolean;
  /** Enable perspective correction in review (default: true) */
  perspectiveCorrection?: boolean;
  /** Enable dark mode enhancement: auto-torch, low-light warning (default: true) */
  darkModeEnhance?: boolean;
  /** Crop captured photo to guide frame (default: true) */
  cropToGuide?: boolean;
  /** Review overlay button layout (default: 'default') */
  reviewButtons?: 'default' | 'captureAnotherDone' | 'doneOnly';
  /** Capture mode selector */
  captureMode?: 'single' | 'combine' | 'multiple';
  /** Called when captureMode='multiple' and user taps Done */
  onBatchComplete?: (photos: string[]) => void;
  /** Contextual quick-action shortcuts */
  shortcuts?: CaptureShortcut[];
  /** 2×2 grid of capture mode buttons */
  captureModeGrid?: CaptureModeButton[];
  /** Read-only reference photos shown at top of viewfinder */
  contextPhotos?: string[];
  /** Expense categorization text shown on review screen */
  expenseContext?: string;
  /** Called after successful capture */
  onAfterCapture?: () => void;
  /** Custom capture button */
  captureButton?: React.ReactNode;
}

export function DocumentCameraModal({
  camera,
  title = 'Take Photo',
  subtitle = 'Position receipt within the frame',
  documentProfile = 'auto',
  guideMode = 'auto',
  autoCapture = true,
  autoZoom = true,
  reviewCapture = true,
  batchCapture = false,
  perspectiveCorrection = true,
  darkModeEnhance = true,
  cropToGuide = true,
  reviewButtons = 'default',
  captureMode,
  onBatchComplete,
  shortcuts,
  captureModeGrid,
  contextPhotos,
  expenseContext,
  onAfterCapture,
  captureButton,
}: DocumentCameraModalProps) {
  return (
    <CameraModal
      camera={camera}
      title={title}
      guide={{ mode: guideMode, title, subtitle }}
      cropToGuide={cropToGuide}
      autoCapture={autoCapture}
      autoZoom={autoZoom}
      reviewCapture={reviewCapture}
      batchCapture={batchCapture}
      perspectiveCorrection={perspectiveCorrection}
      darkModeEnhance={darkModeEnhance}
      documentProfile={documentProfile}
      reviewButtons={reviewButtons}
      captureMode={captureMode}
      onBatchComplete={onBatchComplete}
      shortcuts={shortcuts}
      captureModeGrid={captureModeGrid}
      contextPhotos={contextPhotos}
      expenseContext={expenseContext}
      onAfterCapture={onAfterCapture}
      captureButton={captureButton}
    />
  );
}

export default DocumentCameraModal;
